import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, unref as _unref, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "dx-swatch-additional menu-container" }

import DxTreeView, { DxItem } from "devextreme-vue/tree-view";
import { onMounted, ref, watch } from "vue";
import { RouteLocationRaw, useRoute, useRouter } from "vue-router";
import FindContact from "@/components/find/contacts/find-contact.vue";
import FindProperty from "@/components/find/properties/find-property.vue";
import FindWorkOrder from "@/components/find/work-orders/find-work-order.vue";
import editTaskModal from "@/components/tasks/edit-task-modal.vue";
import { useUserPermissionsStore } from "@/stores/UserPermissionsStore";
import ContactModal from "@/components/contacts/record/contact-modal.vue";
import { UserPermissions } from "@/types/user/permissions/UserPermissions";
import EditNoteModal from "@/components/notes/edit-note-modal.vue";
import NewCertificateModal from "@/components/certificates/new-certificate-modal.vue";
import WorkOrderModal from "@/components/work-orders/work-order-modal.vue";
import EditContactDetails from "@/components/contacts/record/edit-contact-details.vue";
import { RouteNames } from "@/router/RouteNames";
import { useContactRecordNavigationStore } from "@/stores/record-navigation/ContactRecordNavigationStore";
import NewPropertyWizard from "@/components/properties/new-property-wizard.vue";
import { usePropertyRecordNavigationStore } from "@/stores/record-navigation/PropertyRecordNavigationStore";
import displayInfoToast from "@/alerts/infoToastAlert";
import { useSystemConfigurationStore } from "@/stores/SystemConfigurationStore";
import { SystemConfiguration } from "acquaint-api/constants/SystemConfiguration";
import { useLoginStore } from "@/stores/LoginStore";
import { AutoLoginAction } from "@/enums/system/AutoLoginAction";
import MatchMarketingModal from "@/components/match-marketing-modal.vue";
import NewsConfigurationModal from "@/components/website-news/news-configuration-modal.vue";
import MarketingEmailDesignerModal from "@/components/email-marketing/marketing-email-designer-modal.vue";
import QrCodeModal from "@/components/qr-code/qr-code-modal.vue";
import CreateOffersModal from "@/components/offers/create-offers-modal.vue";
import SelectBankAccountModal from "@/components/accounts/select-bank-account-modal.vue";
import ProcessTenantPaymentsModal from "@/components/accounts/process-tenant-payments-modal.vue";
import AiAutoLoginAction from "@/components/ai/ai-auto-login-action.vue";
import ConsolidatedPaymentRunModal from "@/components/financials/consolidated-payment-run-modal.vue";
import SummaryStatementsModal from "@/components/financials/summary-statements-modal.vue";
import PreTenancyModal from "@/components/tenancies/pre-tenancies/pre-tenancy-modal.vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'acquaint-nav-menu',
  props: {
  /**
   * Determines whether the component should be displayed in a compact mode.
   * @type {boolean}
   * @required
   */
  compactMode: {
    type: Boolean,
    required: true,
  },
},
  emits: [
  /**
   * Emitted when a generic click action occurs.
   * Can be used for handling click events in the parent component.
   */
  "click",

  /**
   * Emitted when the navigation menu should be closed.
   * Typically used for handling menu visibility on smaller screens.
   */
  "closeNavigationMenu",

  /**
   * Emitted when switching to a tenant profile.
   * Sends relevant IDs for handling tenant-related actions.
   * @param {number} contactId - The ID of the contact associated with the tenant.
   * @param {number} propertyId - The ID of the property linked to the tenant.
   * @param {number} tenancyId - The ID of the tenancy agreement.
   */
  "tenantSwitchClick",

  /**
   * Emitted when switching to a landlord profile.
   * Sends relevant property and tenancy details.
   * @param {number} propertyId - The ID of the property linked to the landlord.
   * @param {number} tenancyId - The ID of the tenancy agreement.
   */
  "landlordSwitchClick",

  /**
   * Emitted when switching to a purchaser profile.
   * Sends contact and property details related to the purchaser.
   * @param {number} contactId - The ID of the contact associated with the purchaser.
   * @param {number} propertyId - The ID of the property linked to the purchaser.
   */
  "purchaserSwitchClick",

  /**
   * Emitted when a new support case should be created.
   * Triggers the UI to display the new support case form.
   */
  "showNewSupportCase",
],
  setup(__props, { emit }) {

const props = __props;





const route = useRoute();
const router = useRouter();
const userPermissionsStore = useUserPermissionsStore();
const userPermissions = ref<UserPermissions>();
const contactRecordNavigationStore = useContactRecordNavigationStore();
const propertyRecordNavigationStore = usePropertyRecordNavigationStore();
const owner = ref(0);
const treeViewRef = ref<InstanceType<typeof DxTreeView> | null>(null);
const systemConfigStore = useSystemConfigurationStore();
const loginStore = useLoginStore();
const showUtilitySwitchingOptions = ref(false);
const displayMatchMarketingModal = ref(false);
const displayNewsConfigurationModal = ref(false);
const displayMarketingEmailDesignerModal = ref(false);
const displayQRGeneratorModal = ref(false);
const displayOffersModal = ref(false);
const displayBankModal = ref(false);
const displayTenantPaymentsModal = ref(false);
const displaySummaryStatementsModal = ref(false);
const displayConsolidatedPaymentRunModal = ref(false);
const selectedBankAccount = ref(0);

function forwardClick(...args: any[]) {
  emit("click", args);
}

function handleItemClick(e: {
  itemData: { path: RouteLocationRaw };
  event: any;
  node: any;
}) {
  if (!e.itemData.path || props.compactMode) {
    return;
  }
  router.push(e.itemData.path);

  const pointerEvent = e.event;
  pointerEvent.stopPropagation();
}

function updateSelection() {
  if (!treeViewRef.value || !treeViewRef.value.instance) {
    return;
  }

  treeViewRef.value.instance.selectItem(route.path);
  treeViewRef.value.instance.expandItem(route.path);
}

onMounted(async () => {
  updateSelection();

  await getUserPermissions();

  showUtilitySwitchingOptions.value =
    !!(await systemConfigStore.getSystemConfigurationValue(
      SystemConfiguration.PropertyButtonID,
    ));

  if (props.compactMode) {
    treeViewRef.value.instance.collapseAll();
  }

  processAutoLoginAction();
});

const onTreeViewItemExpanded = (e: any) => {
  // Ignore programmatic expansion to avoid undesirable behaviour
  if (e.event?.type != "dxclick") {
    return;
  }

  if (isScrollTreeViewRequired(e.itemElement)) {
    treeViewRef.value.instance.scrollToItem(e.node.key);
  }
};

const isScrollTreeViewRequired = (itemElement: HTMLElement) => {
  const rect = itemElement.getBoundingClientRect();

  const headerElement = document.querySelector(
    ".header-component.layout-header",
  );

  /* As the treeview starts below the header, we need to add the header height to the calculation
   * If, for whatever reason, the header isn't found, use a default offset
   * This ensures we always scroll items into view when required */
  const headerHeight = headerElement?.clientHeight ?? 55;

  // Checks whether the expanded menu appears off the bottom the screen
  if (rect.y + rect.height + headerHeight > window.innerHeight) {
    return true;
  }

  // Checks whether the item is appears off the top of the screen
  return rect.y < headerHeight;
};

const aiAutoLoginActionRef = ref<InstanceType<typeof AiAutoLoginAction> | null>(
  null,
);

const processAutoLoginAction = () => {
  const autoLoginData = loginStore.getAutoLoginData();

  if (!autoLoginData) {
    return;
  }

  switch (autoLoginData.action) {
    case AutoLoginAction.SwitchToTenant:
      expandUtilitySwitchingMenu.value = true;
      emit(
        "tenantSwitchClick",
        autoLoginData.contactId,
        autoLoginData.propertyId,
        autoLoginData.tenancyId,
      );
      break;
    case AutoLoginAction.SwitchToLandlord:
      expandUtilitySwitchingMenu.value = true;
      emit(
        "landlordSwitchClick",
        autoLoginData.propertyId,
        autoLoginData.tenancyId,
      );
      break;
    case AutoLoginAction.SwitchToPurchaser:
      expandUtilitySwitchingMenu.value = true;
      emit(
        "purchaserSwitchClick",
        autoLoginData.contactId,
        autoLoginData.propertyId,
      );
      break;
    case AutoLoginAction.SwitchesList:
      expandUtilitySwitchingMenu.value = true;
      router.push({ name: RouteNames.UtilitySwitchesList });
      break;
    case AutoLoginAction.SupportCasesList:
      expandUtilitySwitchingMenu.value = true;
      router.push({ name: RouteNames.SupportCases });
      break;
    case AutoLoginAction.CreateSupportCase:
      expandUtilitySwitchingMenu.value = true;
      emit("showNewSupportCase");
      break;
    case AutoLoginAction.AIImageEnhancement:
    case AutoLoginAction.AIPropertyListing:
      /* Somewhat strange. As we don't want to store the auto login action to prevent it being processed multiple times,
       * we must call the performAiAutoAction function on the component directly and pass the relevant data, rather than using props.
       * This is because the auto login action data will be cleared before the component can react to prop changes. */
      aiAutoLoginActionRef.value?.performAiAutoAction(
        autoLoginData.propertyId ?? 0,
        autoLoginData.action,
      );
      break;
  }

  // To prevent the auto login action being processed multiple times, reset it to None
  loginStore.resetAutoLoginAction();
};

// As the user may not have permission to edit the personal info we should disable the edit button
const getUserPermissions = async () => {
  userPermissions.value = await userPermissionsStore.getUserPermissions();
};

watch(
  () => route.path,
  () => {
    updateSelection();
  },
);

watch(
  () => props.compactMode,
  () => {
    if (props.compactMode) {
      treeViewRef.value.instance.collapseAll();
    } else {
      updateSelection();
    }
  },
);

const showCreateTaskModal = ref(false);
const showFindContactModal = ref(false);
const showFindPropertyModal = ref(false);
const showFindWorkOrderModal = ref(false);
const showNewContactModal = ref(false);
const showNewNoteModal = ref(false);
const showNewCertificateModal = ref(false);
const showNewWorkOrderModal = ref(false);
const showNewPreTenancyModal = ref(false);
const preTenancyModalKey = ref(0);
const displayEditContactModal = ref(false);
const reDisplayFindContactModal = ref(false);
const showNewPropertyModal = ref(false);
const expandUtilitySwitchingMenu = ref(false);

const createTask = () => (showCreateTaskModal.value = true);

// If a user has started to create a new contact, chosen to edit an already existing record, the edit modal must be opened with this selected
// contact's details.

const editContactModalId = ref(0);

const openEditContactModal = (id: number) => {
  editContactModalId.value = id;
  displayEditContactModal.value = true;
};

const contactCreated = (newContactId: number, createNewProperty: boolean) => {
  if (!newContactId && reDisplayFindContactModal.value) {
    showFindContactModal.value = true;
    reDisplayFindContactModal.value = false;
    return;
  }

  if (!newContactId) {
    return;
  }

  if (createNewProperty) {
    owner.value = newContactId;
    showNewPropertyModal.value = true;
    return;
  }

  // If a user accesses a contact page and then creates a new one, we want to clear the records so that the navigation buttons don't appear.
  // Otherwise they will, and the old count of contacts and the index of the contact the user was on will appear on the new contact's page.
  contactRecordNavigationStore.clear();
  router.push({
    name: RouteNames.ContactPage,
    params: { contactid: newContactId.toString() },
  });
  reDisplayFindContactModal.value = false;
};

const openNewContactModal = () => {
  reDisplayFindContactModal.value = true;
  showNewContactModal.value = true;
};

const newPropertyWizardClose = async (
  newProperty: boolean,
  newPropertyId: number,
) => {
  if (!newProperty) {
    return;
  }

  // Cache the properties so users can navigate between records on the property page
  propertyRecordNavigationStore.cacheProperties([newPropertyId], 0);
  router.push({
    name: RouteNames.PropertyPage,
    params: { propertyid: newPropertyId.toString() },
  });
};

const createNewProperty = () => {
  // We must reset this to 0 so the owner field is cleared
  owner.value = 0;
  showNewPropertyModal.value = true;
};

const onNewAppointmentClick = (e: any) => {
  if (screen.width < 600) {
    emit("closeNavigationMenu", e);
  }

  if (router.currentRoute.value.name == RouteNames.Diary) {
    screen.width < 992
      ? displayInfoToast("Double tap on a time slot to create an Appointment.")
      : displayInfoToast(
          "Double click on a time slot to create an Appointment.",
        );
  } else {
    router.push({ name: RouteNames.Diary });
  }
};

const onNewPropertyOfferClick = () => (displayOffersModal.value = true);

const onSwitchToTenantClick = () => emit("tenantSwitchClick");
const onSwitchToLandlordClick = () => emit("landlordSwitchClick");
const onSwitchToPurchaserClick = () => emit("purchaserSwitchClick");

const openTcfPdf = () => {
  window.open(
    "https://supplierserviceterms.z33.web.core.windows.net/Tenant_Consent_Form.pdf",
  );
};

const bankAccountSelected = (bankAccountId: number) => {
  selectedBankAccount.value = bankAccountId;
  displayTenantPaymentsModal.value = true;
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "dx-swatch side-navigation-menu",
    onClick: forwardClick
  }, [
    _renderSlot(_ctx.$slots, "default"),
    _createElementVNode("div", _hoisted_1, [
      _createVNode(_unref(DxTreeView), {
        ref_key: "treeViewRef",
        ref: treeViewRef,
        "key-expr": "path",
        "selection-mode": "single",
        "focus-state-enabled": false,
        "expand-event": "click",
        onItemClick: handleItemClick,
        onItemExpanded: onTreeViewItemExpanded,
        width: "100%"
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(DxItem), {
            icon: "/icons/home.svg",
            text: "Home",
            path: "/home"
          }),
          _createVNode(_unref(DxItem), {
            icon: "fas fa-bell",
            text: "Reminders",
            path: "/reminders"
          }),
          _createVNode(_unref(DxItem), {
            icon: "fas fa-calendar-alt",
            text: "Diary",
            path: "/diary"
          }),
          _createVNode(_unref(DxItem), {
            icon: "fas fa-magnifying-glass",
            text: "Find"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(DxItem), {
                icon: "far fa-user",
                text: "Contact",
                onClick: _cache[0] || (_cache[0] = ($event: any) => (showFindContactModal.value = true))
              }),
              _createVNode(_unref(DxItem), {
                icon: "far fa-house",
                text: "Property",
                onClick: _cache[1] || (_cache[1] = ($event: any) => (showFindPropertyModal.value = true))
              }),
              _createVNode(_unref(DxItem), {
                icon: "far fa-screwdriver-wrench",
                text: "Work Order",
                onClick: _cache[2] || (_cache[2] = ($event: any) => (showFindWorkOrderModal.value = true))
              })
            ]),
            _: 1
          }),
          (showUtilitySwitchingOptions.value)
            ? (_openBlock(), _createBlock(_unref(DxItem), {
                key: 0,
                icon: "fas fa-wrench",
                text: "Utility Switching",
                expanded: expandUtilitySwitchingMenu.value,
                "onUpdate:expanded": _cache[3] || (_cache[3] = ($event: any) => ((expandUtilitySwitchingMenu).value = $event))
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(DxItem), {
                    icon: "/icons/TenantSwitch.svg",
                    text: "Switch to Tenant",
                    onClick: onSwitchToTenantClick
                  }),
                  _createVNode(_unref(DxItem), {
                    icon: "/icons/LandlordSwitch.svg",
                    text: "Switch to Landlord",
                    onClick: onSwitchToLandlordClick
                  }),
                  _createVNode(_unref(DxItem), {
                    icon: "/icons/PurchaserSwitch.svg",
                    text: "Switch to Purchaser",
                    onClick: onSwitchToPurchaserClick
                  }),
                  _createVNode(_unref(DxItem), {
                    icon: "/icons/Switchover.svg",
                    text: "Switches",
                    path: "/switches"
                  }),
                  _createVNode(_unref(DxItem), {
                    icon: "/icons/SupportCase.svg",
                    text: "Support Cases",
                    path: "/support-cases"
                  }),
                  _createVNode(_unref(DxItem), {
                    icon: "/icons/Document.svg",
                    text: "Consent Form",
                    onClick: openTcfPdf
                  })
                ]),
                _: 1
              }, 8, ["expanded"]))
            : _createCommentVNode("", true),
          _createVNode(_unref(DxItem), {
            icon: "fas fa-plus",
            text: "Create New"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(DxItem), {
                icon: "far fa-calendar-plus",
                text: "Appointment",
                disabled: !userPermissions.value?.appointments.new,
                onClick: _cache[4] || (_cache[4] = (e) => onNewAppointmentClick(e))
              }, null, 8, ["disabled"]),
              _createVNode(_unref(DxItem), {
                icon: "far fa-user-plus",
                text: "Contact",
                onClick: _cache[5] || (_cache[5] = ($event: any) => (showNewContactModal.value = true)),
                disabled: !userPermissions.value?.contacts.new
              }, null, 8, ["disabled"]),
              _createVNode(_unref(DxItem), {
                icon: "far fa-house-chimney-medical",
                text: "Property",
                onClick: createNewProperty,
                disabled: !userPermissions.value?.properties.new
              }, null, 8, ["disabled"]),
              _createVNode(_unref(DxItem), {
                icon: "far fa-file-lines",
                text: "Note",
                onClick: _cache[6] || (_cache[6] = ($event: any) => (showNewNoteModal.value = true)),
                disabled: !userPermissions.value?.correspondence.new
              }, null, 8, ["disabled"]),
              _createVNode(_unref(DxItem), {
                icon: "far fa-square-check",
                disabled: !userPermissions.value?.correspondence.new,
                text: "Task",
                onClick: _cache[7] || (_cache[7] = ($event: any) => (createTask()))
              }, null, 8, ["disabled"]),
              _createVNode(_unref(DxItem), {
                icon: "far fa-file-certificate",
                text: "Certificate",
                onClick: _cache[8] || (_cache[8] = ($event: any) => (showNewCertificateModal.value = true))
              }),
              _createVNode(_unref(DxItem), {
                icon: "far fa-screwdriver-wrench",
                text: "Work Order",
                onClick: _cache[9] || (_cache[9] = ($event: any) => (showNewWorkOrderModal.value = true))
              }),
              _createVNode(_unref(DxItem), {
                icon: "far fa-tag",
                disabled: !userPermissions.value?.properties.new,
                text: "Property Offer",
                onClick: _cache[10] || (_cache[10] = ($event: any) => (onNewPropertyOfferClick()))
              }, null, 8, ["disabled"]),
              _createVNode(_unref(DxItem), {
                icon: "far fa-house-chimney-user",
                disabled: !userPermissions.value?.tenancies.new,
                text: "Pre-Tenancy",
                onClick: _cache[11] || (_cache[11] = ($event: any) => (showNewPreTenancyModal.value = true))
              }, null, 8, ["disabled"])
            ]),
            _: 1
          }),
          _createVNode(_unref(DxItem), {
            icon: "fas fa-address-book",
            text: "Contacts"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(DxItem), {
                icon: "far fa-users",
                text: "Contact List",
                path: "/contacts"
              }),
              _createVNode(_unref(DxItem), {
                icon: "far fa-people-roof",
                text: "Applicant List",
                path: "/applicants"
              })
            ]),
            _: 1
          }),
          _createVNode(_unref(DxItem), {
            icon: "fas fa-house",
            text: "Properties"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(DxItem), {
                icon: "/icons/houses.svg",
                text: "Property List",
                path: "/properties"
              }),
              _createVNode(_unref(DxItem), {
                icon: "far fa-book",
                text: "Journal",
                path: "/journal"
              }),
              _createVNode(_unref(DxItem), {
                icon: "far fa-arrows-spin",
                text: "Progression List",
                path: "/progressions"
              }),
              _createVNode(_unref(DxItem), {
                icon: "far fa-globe-pointer",
                text: "Website Views List",
                path: "/web-viewings"
              }),
              _createVNode(_unref(DxItem), {
                icon: "far fa-tag",
                text: "Property Offers",
                path: "/property-offers"
              }),
              _createVNode(_unref(DxItem), {
                icon: "far fa-house-flag",
                text: "Property Marketing List",
                path: "/property-marketing"
              }),
              _createVNode(_unref(DxItem), {
                icon: "far fa-bullhorn",
                text: "Marketing"
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(DxItem), {
                    icon: "far fa-newspaper",
                    text: "Website News",
                    onClick: _cache[12] || (_cache[12] = () => (displayNewsConfigurationModal.value = true))
                  }),
                  _createVNode(_unref(DxItem), {
                    icon: "far fa-house-user",
                    text: "Match & Marketing",
                    onClick: _cache[13] || (_cache[13] = ($event: any) => (displayMatchMarketingModal.value = true))
                  }),
                  _createVNode(_unref(DxItem), {
                    icon: "far fa-envelope",
                    text: "Marketing Email Designer",
                    onClick: _cache[14] || (_cache[14] = ($event: any) => (displayMarketingEmailDesignerModal.value = true))
                  }),
                  _createVNode(_unref(DxItem), {
                    icon: "far fa-qrcode",
                    text: "QR Code Generator",
                    onClick: _cache[15] || (_cache[15] = ($event: any) => (displayQRGeneratorModal.value = true))
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          }),
          _createVNode(_unref(DxItem), {
            icon: "fas fa-faucet-drip",
            text: "Property Management"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(DxItem), {
                icon: "far fa-screwdriver-wrench",
                text: "Work Orders",
                path: "/work-orders"
              }),
              _createVNode(_unref(DxItem), {
                icon: "far fa-screwdriver-wrench",
                text: "Maintenance Requests",
                path: "/maintenance-requests"
              }),
              _createVNode(_unref(DxItem), {
                icon: "far fa-key",
                text: "Keys",
                path: "/key-list"
              })
            ]),
            _: 1
          }),
          _createVNode(_unref(DxItem), {
            icon: "fas fa-coins",
            text: "Financial"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(DxItem), {
                icon: "far fa-hand-holding-dollar",
                text: "Process Tenant Payments",
                onClick: _cache[16] || (_cache[16] = ($event: any) => (displayBankModal.value = true))
              }),
              _createVNode(_unref(DxItem), {
                icon: "far fa-file-invoice-dollar",
                text: "Account Summary Statements",
                onClick: _cache[17] || (_cache[17] = ($event: any) => (displaySummaryStatementsModal.value = true))
              }),
              _createVNode(_unref(DxItem), {
                icon: "far fa-sack-dollar",
                text: "Payment Runs"
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(DxItem), {
                    icon: "far fa-building-columns",
                    text: "Payment Run",
                    path: "/financial2a"
                  }),
                  _createVNode(_unref(DxItem), {
                    icon: "far fa-building-columns",
                    text: "Consolidated Payment Run",
                    disabled: !userPermissions.value?.financialOptions.paymentrun,
                    onClick: _cache[18] || (_cache[18] = ($event: any) => (displayConsolidatedPaymentRunModal.value = true))
                  }, null, 8, ["disabled"]),
                  _createVNode(_unref(DxItem), {
                    icon: "far fa-building-columns",
                    text: "Payment Run List",
                    path: "/financial2"
                  })
                ]),
                _: 1
              }),
              false
                ? (_openBlock(), _createBlock(_unref(DxItem), {
                    key: 0,
                    icon: "far fa-sack-dollar",
                    text: "Debt Management"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_unref(DxItem), {
                        icon: "far fa-coins",
                        text: "Manage Debts",
                        path: "/financial5"
                      }),
                      _createVNode(_unref(DxItem), {
                        icon: "far fa-calendar",
                        text: "Consolidated Dates",
                        path: "/financial6"
                      }),
                      _createVNode(_unref(DxItem), {
                        icon: "far fa-coins",
                        text: "Charge Interest on Arrears",
                        path: "/financial7"
                      })
                    ]),
                    _: 1
                  }))
                : _createCommentVNode("", true)
            ]),
            _: 1
          }),
          _createVNode(_unref(DxItem), {
            icon: "fas fa-file-certificate",
            text: "Certificate List",
            path: "/certificates"
          }),
          _createVNode(_unref(DxItem), {
            icon: "/icons/TenancyListIcon.svg",
            text: "Tenancy List",
            path: "/tenancy-list"
          }),
          _createVNode(_unref(DxItem), {
            icon: "fas fa-calendar-check",
            text: "Appointment List",
            path: "/appointments"
          }),
          _createVNode(_unref(DxItem), {
            icon: "fas fa-list-check",
            text: "Task List",
            path: "/tasks"
          })
        ]),
        _: 1
      }, 512)
    ]),
    _createVNode(EditContactDetails, {
      contactId: editContactModalId.value,
      modelValue: displayEditContactModal.value,
      "onUpdate:modelValue": _cache[19] || (_cache[19] = ($event: any) => ((displayEditContactModal).value = $event))
    }, null, 8, ["contactId", "modelValue"]),
    _createVNode(editTaskModal, {
      id: 0,
      modelValue: showCreateTaskModal.value,
      "onUpdate:modelValue": _cache[20] || (_cache[20] = ($event: any) => ((showCreateTaskModal).value = $event))
    }, null, 8, ["modelValue"]),
    _createVNode(FindContact, {
      modelValue: showFindContactModal.value,
      "onUpdate:modelValue": _cache[21] || (_cache[21] = ($event: any) => ((showFindContactModal).value = $event)),
      onOpenNewContactModal: openNewContactModal,
      showNewContactButton: userPermissions.value?.properties.new ?? false
    }, null, 8, ["modelValue", "showNewContactButton"]),
    _createVNode(FindProperty, {
      modelValue: showFindPropertyModal.value,
      "onUpdate:modelValue": _cache[22] || (_cache[22] = ($event: any) => ((showFindPropertyModal).value = $event))
    }, null, 8, ["modelValue"]),
    _createVNode(FindWorkOrder, {
      modelValue: showFindWorkOrderModal.value,
      "onUpdate:modelValue": _cache[23] || (_cache[23] = ($event: any) => ((showFindWorkOrderModal).value = $event))
    }, null, 8, ["modelValue"]),
    _createVNode(ContactModal, {
      modelValue: showNewContactModal.value,
      "onUpdate:modelValue": _cache[24] || (_cache[24] = ($event: any) => ((showNewContactModal).value = $event)),
      onOpenEditContactModal: openEditContactModal,
      onContactCreated: contactCreated
    }, null, 8, ["modelValue"]),
    _createVNode(EditNoteModal, {
      modelValue: showNewNoteModal.value,
      "onUpdate:modelValue": _cache[25] || (_cache[25] = ($event: any) => ((showNewNoteModal).value = $event)),
      id: 0
    }, null, 8, ["modelValue"]),
    _createVNode(NewCertificateModal, {
      modelValue: showNewCertificateModal.value,
      "onUpdate:modelValue": _cache[26] || (_cache[26] = ($event: any) => ((showNewCertificateModal).value = $event))
    }, null, 8, ["modelValue"]),
    _createVNode(WorkOrderModal, {
      modelValue: showNewWorkOrderModal.value,
      "onUpdate:modelValue": _cache[27] || (_cache[27] = ($event: any) => ((showNewWorkOrderModal).value = $event))
    }, null, 8, ["modelValue"]),
    _createVNode(NewPropertyWizard, {
      modelValue: showNewPropertyModal.value,
      "onUpdate:modelValue": _cache[28] || (_cache[28] = ($event: any) => ((showNewPropertyModal).value = $event)),
      onModalClosed: newPropertyWizardClose,
      ownerId: owner.value
    }, null, 8, ["modelValue", "ownerId"]),
    _createVNode(MatchMarketingModal, {
      modelValue: displayMatchMarketingModal.value,
      "onUpdate:modelValue": _cache[29] || (_cache[29] = ($event: any) => ((displayMatchMarketingModal).value = $event))
    }, null, 8, ["modelValue"]),
    _createVNode(NewsConfigurationModal, {
      modelValue: displayNewsConfigurationModal.value,
      "onUpdate:modelValue": _cache[30] || (_cache[30] = ($event: any) => ((displayNewsConfigurationModal).value = $event))
    }, null, 8, ["modelValue"]),
    _createVNode(MarketingEmailDesignerModal, {
      modelValue: displayMarketingEmailDesignerModal.value,
      "onUpdate:modelValue": _cache[31] || (_cache[31] = ($event: any) => ((displayMarketingEmailDesignerModal).value = $event))
    }, null, 8, ["modelValue"]),
    _createVNode(QrCodeModal, {
      modelValue: displayQRGeneratorModal.value,
      "onUpdate:modelValue": _cache[32] || (_cache[32] = ($event: any) => ((displayQRGeneratorModal).value = $event))
    }, null, 8, ["modelValue"]),
    _createVNode(CreateOffersModal, {
      modelValue: displayOffersModal.value,
      "onUpdate:modelValue": _cache[33] || (_cache[33] = ($event: any) => ((displayOffersModal).value = $event))
    }, null, 8, ["modelValue"]),
    (_openBlock(), _createBlock(PreTenancyModal, {
      modelValue: showNewPreTenancyModal.value,
      "onUpdate:modelValue": _cache[34] || (_cache[34] = ($event: any) => ((showNewPreTenancyModal).value = $event)),
      onModalClosed: _cache[35] || (_cache[35] = ($event: any) => (preTenancyModalKey.value++)),
      key: preTenancyModalKey.value
    }, null, 8, ["modelValue"])),
    _createVNode(SelectBankAccountModal, {
      modelValue: displayBankModal.value,
      "onUpdate:modelValue": _cache[36] || (_cache[36] = ($event: any) => ((displayBankModal).value = $event)),
      onBankAccountSelected: bankAccountSelected
    }, null, 8, ["modelValue"]),
    _createVNode(ProcessTenantPaymentsModal, {
      modelValue: displayTenantPaymentsModal.value,
      "onUpdate:modelValue": _cache[37] || (_cache[37] = ($event: any) => ((displayTenantPaymentsModal).value = $event)),
      bankAccountId: selectedBankAccount.value
    }, null, 8, ["modelValue", "bankAccountId"]),
    _createVNode(AiAutoLoginAction, {
      ref_key: "aiAutoLoginActionRef",
      ref: aiAutoLoginActionRef
    }, null, 512),
    _createVNode(SummaryStatementsModal, {
      modelValue: displaySummaryStatementsModal.value,
      "onUpdate:modelValue": _cache[38] || (_cache[38] = ($event: any) => ((displaySummaryStatementsModal).value = $event))
    }, null, 8, ["modelValue"]),
    _createVNode(ConsolidatedPaymentRunModal, {
      modelValue: displayConsolidatedPaymentRunModal.value,
      "onUpdate:modelValue": _cache[39] || (_cache[39] = ($event: any) => ((displayConsolidatedPaymentRunModal).value = $event))
    }, null, 8, ["modelValue"])
  ]))
}
}

})